import { SelectType } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { UseFormReturn } from 'react-hook-form';
import { useEffect, useState } from 'react';

export const useActivityFilters = (): SelectType[] => {
  const { messages } = useLocalization();

  return [
    {
      value: 'name',
      label: messages.eventdetectorsSaveAdvancedActivityFiltersLabelNameChange,
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipNameChange,
    },
    {
      value: 'destination',
      label: messages.eventdetectorsSaveAdvancedActivityFiltersLabelDestinationChange,
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipDestinationChange,
    },
    {
      value: 'draught',
      label: messages.eventdetectorsSaveAdvancedActivityFiltersLabelDraughtChange,
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipDraughtChange,
    },
    {
      value: 'eta',
      label: messages.eventdetectorsSaveAdvancedActivityFiltersLabelETAChange,
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipETAChange,
    },
    {
      value: 'nav_stat',
      label: messages.eventdetectorsSaveAdvancedActivityFiltersLabelNavStatusChange,
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipNavStatusChange,
    },
    {
      value: 'fishing',
      label: messages.eventdetectorsSaveAdvancedActivityFiltersLabelFishing,
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipFishingChange,
    },
    {
      value: 'transponder_off',
      label: messages.eventdetectorsSaveAdvancedActivityFiltersLabelTransponderOff,
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipTransponderOff,
    },
  ];
};

export type ActivityFilter = {
  static: {
    destination: boolean;
    name: boolean;
    draught: boolean;
    eta: boolean;
  };
  dynamic: {
    nav_stat: boolean;
  };
  fishing: boolean;
  transponder_off: boolean | string;
};

export const convertActivityFilters = (
  activityFilters: SelectType[],
  transponderOffDuration?: string
): ActivityFilter | null => {
  const result: ActivityFilter = {
    static: {
      destination: false,
      name: false,
      draught: false,
      eta: false,
    },
    dynamic: {
      nav_stat: false,
    },
    fishing: false,
    transponder_off: false,
  };

  // Define which values belong to static and dynamic categories
  const staticValues = ['destination', 'name', 'draught', 'eta'];
  const dynamicValues = ['nav_stat'];

  activityFilters.forEach((activityFilter) => {
    const valueKey = activityFilter.value; // Convert value to lowercase and replace spaces with underscores

    if (staticValues.includes(activityFilter.value)) {
      // Set default boolean values based on your example
      result.static[valueKey as 'destination' | 'name' | 'draught' | 'eta'] =
        true;
    } else if (dynamicValues.includes(activityFilter.value)) {
      result.dynamic.nav_stat = true;
    } else if (valueKey === 'fishing') {
      result.fishing = true;
    } else if (valueKey === 'transponder_off' && transponderOffDuration) {
      result.transponder_off = transponderOffDuration;
    }
  });

  // Check if all values in result are false, if so return null
  const allFalse =
    Object.values(result.static).every((value) => !value) &&
    !result.dynamic.nav_stat &&
    !result.fishing &&
    !result.transponder_off;

  return allFalse ? null : result;
};

export const reverseConvertActivityFilters = (
  activityFilter: ActivityFilter | undefined,
  activityFilters: SelectType[]
): SelectType[] => {
  const selectTypes: SelectType[] = [];
  if (!activityFilter) {
    return selectTypes;
  }

  const addFilterIfActive = (key: string) => {
    const filter = activityFilters.find((filter) => filter.value === key);
    if (
      filter &&
      activityFilter.static[key as keyof typeof activityFilter.static]
    ) {
      selectTypes.push(filter);
    }
  };

  // Handle static properties
  Object.keys(activityFilter.static).forEach(addFilterIfActive);

  // Handle dynamic properties
  if (activityFilter.dynamic.nav_stat) {
    const navStatFilter = activityFilters.find(
      (filter) => filter.value === 'nav_stat'
    );
    if (navStatFilter) {
      selectTypes.push(navStatFilter);
    }
  }

  // Handle root-level boolean properties
  ['fishing', 'transponder_off'].forEach((key) => {
    if (
      activityFilter[key as keyof Omit<ActivityFilter, 'static' | 'dynamic'>]
    ) {
      const filter = activityFilters.find((filter) => filter.value === key);
      if (filter) {
        selectTypes.push(filter);
      }
    }
  });

  return selectTypes;
};

type FieldToAccordionMap<T> = {
  [K in keyof T]?: string;
};

export function useErrorNavigation<T extends object>(
  form: UseFormReturn<T>,
  fieldToAccordionMap: FieldToAccordionMap<T>,
  defaultSection = 'detector-item'
) {
  const [openSection, setOpenSection] = useState<string>(defaultSection);

  useEffect(() => {
    if (Object.keys(form.formState.errors).length > 0) {
      const firstError = Object.keys(form.formState.errors)[0] as keyof T;

      // Check if the error field is in our mapping
      const accordionValue = fieldToAccordionMap[firstError] || defaultSection;

      setOpenSection(accordionValue);
    }
  }, [form.formState.errors, fieldToAccordionMap, defaultSection]);

  return { openSection, setOpenSection };
}
